/**
 * Vault Performance Service
 *
 * Chart data: Supabase Edge Function (get-vault-chart-data) for historical data.
 * NAV per share: Express proxy on Vercel (/api/folio/performance) for real-time price.
 */

import { supabase } from "./supabaseService";
import { withPerformanceTracking } from "./performanceMonitoring";

// =============================================================================
// Types
// =============================================================================

export interface VaultChartDataPoint {
  timestamp: string;
  price: number;  // nav_per_share
  change24h: number;
}

export interface VaultPerformanceMetadata {
  vaultAddress: string;
  timeRange: string;
  dataPoints: number;
  latestTimestamp: string | null;
  latestPrice: number | null;
}

export interface VaultChartResponse {
  success: boolean;
  data?: VaultChartDataPoint[];
  metadata?: VaultPerformanceMetadata;
  error?: string;
}

export interface VaultDataSourceInfo {
  source: 'supabase-vault';
  lastUpdated: string;
  dataPoints: number;
  isHealthy: boolean;
}

export interface VaultPerformanceResult {
  chartData: VaultChartDataPoint[];
  currentPrice: number;
  sourceInfo: VaultDataSourceInfo;
}

// =============================================================================
// Constants
// =============================================================================

// Valid time ranges (must match Edge Function)
const VALID_TIME_RANGES = ['24H', '48H', '7D', '30D', '3M', '6M', '1Y', '3Y', 'ALL'];

// =============================================================================
// Functions
// =============================================================================

/**
 * Fetch vault chart data from Supabase Edge Function
 *
 * @param vaultAddress - Ethereum address of the vault (0x...)
 * @param timeRange - Time range for chart data ('24H', '48H', '7D', '30D')
 * @returns Chart data points and metadata
 */
export async function fetchVaultChartData(
  vaultAddress: string,
  timeRange: string = '30D'
): Promise<VaultPerformanceResult> {
  return withPerformanceTracking(
    'vault_fetch_chart_data',
    async () => {
      // Input validation
      if (!vaultAddress || typeof vaultAddress !== 'string') {
        throw new Error('Vault address is required and must be a string');
      }

      // Validate vault address format (0x + 40 hex chars)
      if (!/^0x[a-fA-F0-9]{40}$/.test(vaultAddress)) {
        throw new Error(
          `Invalid vault address format: ${vaultAddress}. Must be 0x followed by 40 hexadecimal characters.`
        );
      }

      // Validate time range
      if (!VALID_TIME_RANGES.includes(timeRange)) {
        throw new Error(
          `Invalid time range: ${timeRange}. Must be one of: ${VALID_TIME_RANGES.join(', ')}`
        );
      }

      // Call Edge Function
      const { data, error } = await supabase.functions.invoke('get-vault-chart-data', {
        body: { vaultAddress, timeRange }
      });

      if (error) {
        throw new Error(`Failed to fetch vault chart data: ${error.message}`);
      }

      const response = data as VaultChartResponse;

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch vault chart data');
      }

      const chartData = response.data || [];
      const metadata = response.metadata;

      return {
        chartData,
        currentPrice: metadata?.latestPrice || (chartData.length > 0 ? chartData[chartData.length - 1].price : 0),
        sourceInfo: {
          source: 'supabase-vault',
          lastUpdated: metadata?.latestTimestamp || new Date().toISOString(),
          dataPoints: chartData.length,
          isHealthy: chartData.length > 0
        }
      };
    },
    { vaultAddress, timeRange }
  );
}

/**
 * Get the latest vault price (NAV per share)
 *
 * @param vaultAddress - Ethereum address of the vault
 * @returns Latest price and metadata
 */
export async function getLatestVaultPrice(
  vaultAddress: string
): Promise<{
  price: number;
  lastUpdated: string | null;
} | null> {
  return withPerformanceTracking(
    'vault_get_latest_price',
    async () => {
      try {
        // Fetch minimal data (24H range should be sufficient for latest price)
        const result = await fetchVaultChartData(vaultAddress, '24H');

        if (result.chartData.length === 0) {
          return null;
        }

        return {
          price: result.currentPrice,
          lastUpdated: result.sourceInfo.lastUpdated
        };
      } catch (error) {
        return null;
      }
    },
    { vaultAddress }
  );
}

/**
 * Health check for vault performance data
 *
 * @param vaultAddress - Ethereum address of the vault
 * @returns Health status
 */
export async function vaultPerformanceHealthCheck(
  vaultAddress: string
): Promise<{
  status: 'healthy' | 'no_data' | 'error';
  lastDataPoint: string | null;
  dataPoints: number;
  connected: boolean;
  error?: string;
}> {
  return withPerformanceTracking(
    'vault_health_check',
    async () => {
      try {
        const result = await fetchVaultChartData(vaultAddress, '24H');

        return {
          status: result.chartData.length > 0 ? 'healthy' : 'no_data',
          lastDataPoint: result.sourceInfo.lastUpdated,
          dataPoints: result.chartData.length,
          connected: true
        };
      } catch (error) {
        return {
          status: 'error',
          lastDataPoint: null,
          dataPoints: 0,
          connected: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },
    { vaultAddress }
  );
}

// =============================================================================
// Real-Time NAV Per Share (External API)
// =============================================================================

/**
 * Response structure from /vault/performance endpoint
 *
 * Several fields are null for new vaults or when there is insufficient
 * historical data to compute them (e.g. performance since inception).
 */
export interface VaultPerformanceResponse {
  success: boolean;
  data?: {
    vaultAddress: string;
    navPerShare: string | null;
    inceptionNavPerShare: string | null;
    performanceSinceInceptionPct: string | null;
    apySinceInceptionPct: string | null;
    totalValueUsd: string;
    totalSupply: string;
    totalSupplyFormatted: string;
    inception: { date: string; block: string; transaction: string } | null;
    holdings: Array<{
      token: string;
      balance: string;
      priceUsd: string;
      valueUsd: string;
    }>;
    timestamp: string;
  };
  error?: string;
}

/**
 * Fetch real-time NAV per share via Express proxy on Vercel
 *
 * Used to calculate current portfolio value independent of earnings history.
 * Returns the current price per token (NAV per share) from the vault contract.
 *
 * @param vaultAddress - Ethereum address of the vault (0x...)
 * @returns NAV per share as a number
 * @throws Error if API call fails or returns invalid data
 */
export async function getVaultNavPerShare(
  vaultAddress: string,
  chainId: number,
): Promise<number> {
  return withPerformanceTracking(
    'vault_get_nav_per_share',
    async () => {
      // Validate vault address format
      if (!vaultAddress || !/^0x[a-fA-F0-9]{40}$/.test(vaultAddress)) {
        throw new Error(`Invalid vault address: ${vaultAddress}`);
      }

      const params = new URLSearchParams({ chainId: String(chainId), folioAddress: vaultAddress });
      const response = await fetch(`/api/folio/performance?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Folio performance API error: ${response.status}`);
      }

      const result: VaultPerformanceResponse = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch vault performance');
      }

      const { navPerShare: navStr } = result.data;

      if (navStr === null) {
        throw new Error('NAV per share not yet available for this vault');
      }

      const navPerShare = parseFloat(navStr);

      if (isNaN(navPerShare) || navPerShare <= 0) {
        throw new Error(`Invalid NAV per share: ${navStr}`);
      }

      return navPerShare;
    },
    { vaultAddress }
  );
}

// =============================================================================
// Trailing N-Month Return
// =============================================================================

/**
 * Trailing return over the last `months` (default 36), computed against the
 * chart's full history — backfilled backtest and live-cron data alike, since
 * the backfill is calibrated to connect smoothly with the real series (see
 * migration 074). `chartData` must be sorted oldest-to-newest.
 *
 * Returns null when the series doesn't reach back far enough to cover the
 * full window yet — callers should hide the UI in that case rather than show
 * a return computed over a shorter, unlabeled period.
 */
export function calculateTrailingReturnPct(
  chartData: { timestamp: string; price: number }[],
  months = 36,
): number | null {
  if (chartData.length < 2) return null;

  const lastPoint = chartData[chartData.length - 1];
  const cutoff = new Date(lastPoint.timestamp);
  cutoff.setUTCMonth(cutoff.getUTCMonth() - months);
  const cutoffMs = cutoff.getTime();

  // Walk backward from the start to find the latest point at or before the
  // cutoff — the closest available price to "exactly `months` ago".
  let startPoint: { timestamp: string; price: number } | null = null;
  for (const point of chartData) {
    if (new Date(point.timestamp).getTime() > cutoffMs) break;
    startPoint = point;
  }
  if (!startPoint) return null; // History doesn't reach back far enough yet

  const firstPrice = startPoint.price;
  const lastPrice = lastPoint.price;
  if (!firstPrice || firstPrice <= 0) return null;

  return ((lastPrice / firstPrice) - 1) * 100;
}
